// History out of the app: every set as one CSV row.
//
// PURE ON PURPOSE. No store, no DOM, no clock it was not handed — so the
// quoting, the ordering and the local-date arithmetic can be tested in node,
// and the Timeline only decides WHICH sets go in.
//
// The file is written for a spreadsheet, not for another program: one row per
// set, newest first, the lift by name rather than by an id nobody can read, and
// a byte-order mark so Excel and Numbers open it as UTF-8 instead of mangling a
// lift called "Pause squat — 3s" into Latin-1.

import { rangeTo } from './loading.js';

export const CSV_COLUMNS = ['date', 'time', 'lift', 'weight', 'unit', 'reps', 'reps_to', 'filmed'];

const BOM = '\uFEFF';
const EOL = '\r\n'; // RFC 4180. Every spreadsheet reads it; some read nothing else.

const pad = (n) => String(n).padStart(2, '0');

/** YYYY-MM-DD in the phone's own time zone: the day the set was lifted, not UTC's. */
export function localDate(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** HH:MM, 24-hour, local. */
export function localTime(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ---- which sets ----------------------------------------------------------------
// A period is a pair of LOCAL calendar days, both inclusive, as YYYY-MM-DD —
// '' for an open end. Comparing days as strings rather than instants is what
// keeps a set lifted at 23:55 on the last day inside the period in every time
// zone, and it is also how the lifter thinks: "since the 12th", not "since
// 14:00 UTC on the 11th".

const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The periods the export offers. */
export const PERIODS = [
  { id: 'all', label: 'All time' },
  { id: '30d', label: 'Last 30 days' },
  { id: '12m', label: 'Last 12 months' },
  { id: 'custom', label: 'Custom' },
];

/**
 * The first and last day of a period.
 *
 * Last 30 days is today and the 30 days before it (12 Aug – 11 Sep); last 12
 * months starts on the same date a year ago. A custom period takes whatever
 * valid days it is given, the right way round.
 *
 * @param {'all'|'30d'|'12m'|'custom'} period
 * @returns {{ from: string, to: string }}
 */
export function periodDates(period, { now = new Date(), from = '', to = '' } = {}) {
  const today = localDate(now);
  if (period === '30d' || period === '12m') {
    const d = new Date(now);
    if (period === '30d') d.setDate(d.getDate() - 30);
    else d.setFullYear(d.getFullYear() - 1);
    return { from: localDate(d), to: today };
  }
  if (period === 'custom') {
    let f = DAY.test(from) ? from : '';
    let t = DAY.test(to) ? to : '';
    if (f && t && f > t) [f, t] = [t, f];
    return { from: f, to: t };
  }
  return { from: '', to: '' };
}

/**
 * The sets of one lift (or every lift) inside a period.
 *
 * All time keeps a set with an unreadable date — an export of everything should
 * be everything — and any bounded period leaves it out, because it cannot be
 * placed inside one.
 */
export function selectSets(sets, { lift = null, from = '', to = '' } = {}) {
  return sets.filter((a) => {
    if (lift != null && String(a.lift) !== String(lift)) return false;
    if (!from && !to) return true;
    const day = localDate(new Date(a?.performedAt));
    if (!day) return false;
    return (!from || day >= from) && (!to || day <= to);
  });
}

/**
 * `work-up-history-2026-09-11.csv` for everything, dated the day it was made;
 * `work-up-history-2026-08-12-to-2026-09-11.csv` for a period.
 */
export function historyFilename(now = new Date(), { from = '', to = '' } = {}) {
  if (!from && !to) return `work-up-history-${localDate(now)}.csv`;
  return from
    ? `work-up-history-${from}-to-${to || localDate(now)}.csv`
    : `work-up-history-to-${to}.csv`;
}

/**
 * One field, quoted when it has to be.
 *
 * A comma, a quote or a line break forces quotes, with inner quotes doubled.
 * A TEXT field that starts like a formula (= + - @, or a tab/CR) gets a leading
 * apostrophe: a lift name is typed by a person, and a spreadsheet would run
 * "=HYPERLINK(...)" rather than show it. Numbers never go through that branch,
 * so a weight stays a number.
 */
export function csvField(value, { text = false } = {}) {
  let s = value == null ? '' : String(value);
  if (text && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** A weight without float noise or a trailing `.0`: 122.5, 180, 0.25. */
const num = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? String(+v.toFixed(2)) : '';
};

const when = (a) => new Date(a?.performedAt);
const time = (a) => { const t = when(a).getTime(); return Number.isNaN(t) ? -Infinity : t; };

/**
 * The CSV text for a list of attempts — BOM, header, one row per set.
 *
 * @param {object[]} sets    attempts, in any order
 * @param {object}   opts
 * @param {(liftId) => string} opts.liftName  the lift's name as the app shows it
 * @param {(attempt) => string} opts.unitOf   'kg' | 'lb' for that set
 * @returns {string}
 */
export function historyCsv(sets, { liftName = () => '', unitOf = () => '' } = {}) {
  // Newest first; on the same minute, the later record (the higher id) first,
  // which is the order the sets were entered.
  const rows = [...sets].sort((a, b) => time(b) - time(a) || (Number(b.id) || 0) - (Number(a.id) || 0));
  const lines = [CSV_COLUMNS.join(',')];
  for (const a of rows) {
    const d = when(a);
    const reps = Number(a.reps) || 0;
    const to = rangeTo(reps, a.repsTo);
    lines.push([
      csvField(localDate(d)),
      csvField(localTime(d)),
      csvField(liftName(a.lift), { text: true }),
      csvField(num(a.weight)),
      csvField(unitOf(a), { text: true }),
      csvField(reps),
      csvField(to ?? ''),
      csvField(a.videoAssetId ? 'yes' : 'no'),
    ].join(','));
  }
  return BOM + lines.join(EOL) + EOL;
}
